import type { RequestOptions } from '@@/plugin-request/request';
import { history, type RequestConfig } from '@umijs/max';
import { getUserFriendlyError, parseErrorDetails } from './utils/errorMessages';
import { getRequestStatus, getRequestUrl } from './utils/requestError';
import { clearExposedState, getExposedState } from './utils/stateSync';
import { setQueryParams } from './utils/urlHelper';

export const loginPath = '/user/login';
export const skipPaths = [loginPath, '/404'];

const API_BASE_URL = (process.env.UMI_APP_API_BASE_URL || '').trim();
const API_PROXY_PREFIX = (process.env.UMI_APP_API_PROXY_PREFIX || '').trim();

const isAbsoluteUrl = (url: string) => /^(https?:)?\/\//i.test(url);

const withApiBaseUrl = (url: string) => {
  if (isAbsoluteUrl(url)) return url;
  if (!url.startsWith('/v1/')) return url;

  if (API_BASE_URL) {
    const normalizedBase = API_BASE_URL.replace(/\/+$/, '');
    return `${normalizedBase}${url}`;
  }

  // Production-safe fallback: route API calls via same-origin proxy path when available.
  // Keep local development untouched, since Umi dev proxy handles /v1 directly.
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    const isLocalDev = host === 'localhost' || host === '127.0.0.1';
    if (isLocalDev) return url;
  }

  if (!API_PROXY_PREFIX) return url;
  const normalizedPrefix = API_PROXY_PREFIX.startsWith('/')
    ? API_PROXY_PREFIX.replace(/\/+$/, '')
    : `/${API_PROXY_PREFIX.replace(/\/+$/, '')}`;
  if (url.startsWith(`${normalizedPrefix}/`)) return url;
  return `${normalizedPrefix}${url}`;
};

// Match Go BaseResponse[T]
interface ResponseStructure<T = any> {
  code: number;
  success: boolean;
  message: string;
  data: T;
  current?: number;
  pageSize?: number;
  total?: number;
  hasNext?: boolean;
}

enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}

const getNotification = () => {
  try {
    return (window as any).__ANTD_APP__?.notification ?? null;
  } catch (_) {
    return null;
  }
};

const showErrorNotification = (
  errorMessage: string,
  code?: number,
  rawDetails?: string,
) => {
  const parsed = parseErrorDetails(errorMessage);
  const userFriendly = getUserFriendlyError(errorMessage);
  const notificationType = userFriendly.type === 'error' ? 'error' : 'warning';

  const title =
    code || parsed.code ? `Error ${code ?? parsed.code}` : 'Request Error';
  const description = userFriendly.suggestion
    ? `${userFriendly.message}\n\n💡 ${userFriendly.suggestion}`
    : userFriendly.message;

  const notification = getNotification();
  if (notification) {
    notification[notificationType]({
      title,
      description,
      duration: 5,
      placement: 'topRight',
    });
  } else {
    console.error('[Request Error]', {
      title,
      description,
      code: code ?? parsed.code,
      details: rawDetails ?? parsed.details,
    });
  }
};

const getStateForRequest = () => {
  if (typeof window === 'undefined') return undefined;
  return getExposedState();
};

export const interceptorConfig: RequestConfig = {
  requestInterceptors: [
    async (url: string, options: RequestOptions) => {
      const headers = { ...options.headers };
      const state = getStateForRequest();

      // Skip x-igm-site-id header for endpoints that don't require it
      const skipSiteHeader = url.includes('/port/val');
      
      if (state?.currentSite?.id && !headers['x-igm-site-id'] && !skipSiteHeader) {
        headers['x-igm-site-id'] = String(state.currentSite.id);
      }

      if (typeof window !== 'undefined') {
        headers['x-igm-member-auth-location'] = window.location.host;
      }

      return { url: withApiBaseUrl(url), options: { ...options, headers } };
    },
    (url: string, options: RequestOptions) => {
      const params = (options as any).params || {};
      if (Object.keys(params).length === 0) return { url, options };

      const newUrl = setQueryParams(params, url);
      return { url: newUrl, options };
    },
  ],

  responseInterceptors: [
    (response) => {
      const res = response as any;
      // Unwrap actual body — axios wraps in .data, umi may differ
      const body: ResponseStructure = res?.data ?? res;

      if (body?.success === false) {
        showErrorNotification(body.message || 'Unknown error', body.code);
      }

      return response;
    },
  ],
};

export const errorConfig: RequestConfig = {
  errorConfig: {
    errorThrower: (res) => {
      const { success, data, code, message } =
        res as unknown as ResponseStructure;
      if (success) return;

      const error: any = new Error(message);
      error.name = 'BizError';
      error.info = { code, message, data };
      throw error;
    },

    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;

      if (error.name === 'BizError') {
        const { message, code, showType } = error.info ?? {};

        switch (showType) {
          case ErrorShowType.SILENT:
            break;
          case ErrorShowType.WARN_MESSAGE: {
            const n = getNotification();
            n
              ? n.warning({ description: message })
              : console.warn('[Request Warning]', message);
            break;
          }
          case ErrorShowType.ERROR_MESSAGE: {
            const n = getNotification();
            n
              ? n.error({ description: message })
              : console.error('[Request Error]', message);
            break;
          }
          default:
            showErrorNotification(message, code);
        }
        return;
      }

      const status = getRequestStatus(error);
      if (status !== undefined) {
        const data = error.data || error.response?.data;

        switch (status) {
          case 530:
            break;

          case 500:
            showErrorNotification(
              'Sorry, the server is reporting an error. Please try again later.',
              status,
              data?.message,
            );
            break;

          case 401: {
            const requestUrl = getRequestUrl(error);
            const isInternalApi = requestUrl?.includes('/v1/');
            if (!isInternalApi) {
              console.warn(
                '[Auth] Ignored 401 from external source:',
                requestUrl,
              );
              throw error;
            }
            clearExposedState();
            if (skipPaths.includes(history.location.pathname)) return;
            history.replace({ pathname: loginPath, search: '' });
            break;
          }

          case 403:
            if (data?.error === 'totp_setup_required') {
              const isOnSettingsPage =
                window.location.pathname.includes('/account/settings');
              const hasSetupTotpParam =
                window.location.search.includes('setup_totp=true');
              if (!isOnSettingsPage || !hasSetupTotpParam) {
                showErrorNotification(
                  'TOTP Setup Required',
                  403,
                  'You must enable Two-Factor Authentication to access the backoffice.',
                );
                history.push('/account/settings?tab=security&setup_totp=true');
              }
              throw error; // Throw to prevent caller from proceeding
            }

            showErrorNotification(
              'You do not have permission to perform this action.',
              status,
              data?.message,
            );
            break;

          default:
            showErrorNotification(
              `HTTP ${status} Error`,
              status,
              data?.message || error.response?.statusText,
            );
        }
        return;
      }

      if (error.request) {
        showErrorNotification(
          'No response! Please retry.',
          undefined,
          'Network error - no response received',
        );
        return;
      }

      showErrorNotification(
        'Request error, please retry.',
        undefined,
        error.message,
      );
    },
  },
};
